import axios from 'axios';

// Helper function to check if we're in Electron
const isElectron = (): boolean => {
  return typeof window !== 'undefined' && window.electronAPI !== undefined;
};

// Определяем URL API в зависимости от окружения
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  // Если задали явно, используем его
  if (envUrl) {
    return envUrl;
  }

  // Для Electron приложения используем специальный URL
  if (isElectron()) {
    return 'http://task.clowoow.ru:5000/api';
  }

  // Для веб/вебвью по умолчанию ходят в боевой API
  return 'https://prometey-task.clowoow.ru/api';
  // return 'http://task.clowoow.ru:5000/api';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to safely get token from localStorage
// In Electron, localStorage might need a small delay or retry
const getToken = (): string | null => {
  try {
    // Check if localStorage is available
    if (typeof Storage === 'undefined') {
      console.error('localStorage не поддерживается');
      return null;
    }
    
    const token = localStorage.getItem('token');
    
    // In Electron, sometimes localStorage might not be ready immediately
    // Try to verify it's actually working
    if (isElectron() && !token) {
      // Try to test localStorage access
      try {
        localStorage.setItem('__test__', 'test');
        localStorage.removeItem('__test__');
      } catch (e) {
        console.error('localStorage недоступен в Electron:', e);
        return null;
      }
    }
    
    return token;
  } catch (error) {
    console.error('Ошибка при чтении токена из localStorage:', error);
    // Try sessionStorage as fallback
    try {
      return sessionStorage.getItem('token');
    } catch (e) {
      console.error('sessionStorage также недоступен:', e);
      return null;
    }
  }
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle 403 and 401 errors - token might be invalid or expired
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 || error.response?.status === 401) {
      const token = getToken();
      const requestUrl = error.config?.url || 'unknown';
      
      if (!token) {
        console.error(`403/401 ошибка для ${requestUrl}: токен отсутствует в localStorage`);
        console.error('Проверка localStorage:', {
          available: typeof Storage !== 'undefined',
          inElectron: isElectron(),
          electronAPI: typeof window !== 'undefined' ? window.electronAPI : 'undefined'
        });
      } else {
        console.error(`403/401 ошибка для ${requestUrl}: токен присутствует, но сервер отклонил запрос`);
        console.error('Детали токена:', {
          length: token.length,
          prefix: token.substring(0, 20) + '...',
          inElectron: isElectron()
        });
        console.error('Заголовки запроса:', error.config?.headers);
      }
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  username: string;
  email: string;
  role?: string;
  approved?: boolean;
  created_at?: string;
  is_manager?: boolean;
  position?: string | null;
}

export interface Position {
  id: number;
  name: string;
  is_manager: boolean;
}

export interface Task {
  id: number;
  board_id?: number;
  column_id?: number;
  title: string;
  description?: string;
  status: 'open' | 'in_review' | 'completed';
  position?: number;
  created_by: number;
  assigned_to?: number;
  assigned_to_ids?: number[];
  reviewers_ids?: number[];
  assignees?: Array<{ user_id: number; username: string; email: string }>;
  reviewers?: Array<{ user_id: number; username: string; email: string }>;
  created_by_username?: string;
  assigned_to_username?: string;
  deadline?: string | null;
  created_at: string;
  updated_at: string;
  comments?: Comment[];
  attachments?: Attachment[];
  history?: HistoryItem[];
  board_name?: string;
  column_name?: string;
  is_rejected?: number | boolean;
}

export interface Comment {
  id: number;
  task_id: number;
  user_id: number;
  username?: string;
  content: string;
  created_at: string;
}

export interface Attachment {
  id: number;
  task_id: number;
  filename: string;
  original_name: string;
  file_path: string;
  uploaded_by: number;
  uploaded_by_username?: string;
  created_at: string;
}

export interface HistoryItem {
  id: number;
  task_id: number;
  user_id: number;
  username?: string;
  action: string;
  old_value?: string;
  new_value?: string;
  created_at: string;
}

export const authAPI = {
  register: async (username: string, email: string, password: string) => {
    const response = await api.post('/auth/register', { username, email, password });
    return response.data;
  },
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
};

export const tasksAPI = {
  getAll: async (search?: string): Promise<Task[]> => {
    const params = search ? { search } : {};
    const response = await api.get('/tasks', { params });
    return response.data;
  },
  getById: async (id: number): Promise<Task> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },
  create: async (
    title: string, 
    description?: string, 
    assigned_to?: number, 
    assigned_to_ids?: number[],
    reviewers_ids?: number[],
    board_id?: number, 
    column_id?: number, 
    position?: number, 
    deadline?: string
  ): Promise<Task> => {
    const response = await api.post('/tasks', { 
      title, 
      description, 
      assigned_to, 
      assigned_to_ids, 
      reviewers_ids,
      board_id, 
      column_id, 
      position, 
      deadline 
    });
    return response.data;
  },
  update: async (id: number, updates: Partial<Task>): Promise<void> => {
    await api.put(`/tasks/${id}`, updates);
  },
  addComment: async (taskId: number, content: string): Promise<Comment> => {
    const response = await api.post(`/tasks/${taskId}/comments`, { content });
    return response.data;
  },
  updateComment: async (taskId: number, commentId: number, content: string): Promise<Comment> => {
    const response = await api.put(`/tasks/${taskId}/comments/${commentId}`, { content });
    return response.data;
  },
  deleteComment: async (taskId: number, commentId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/comments/${commentId}`);
  },
  uploadAttachment: async (taskId: number, file: File): Promise<Attachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/tasks/${taskId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  deleteAttachment: async (taskId: number, attachmentId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
  },
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/tasks/users/list');
    return response.data;
  },
  move: async (taskId: number, columnId: number, position?: number): Promise<void> => {
    await api.post(`/tasks/${taskId}/move`, { column_id: columnId, position });
  },
  getMyTasks: async (): Promise<Task[]> => {
    const response = await api.get('/tasks/my-tasks');
    return response.data;
  },
  acceptTask: async (taskId: number): Promise<void> => {
    await api.post(`/tasks/${taskId}/accept`);
  },
  sendForReview: async (taskId: number): Promise<void> => {
    await api.post(`/tasks/${taskId}/send-for-review`);
  },
  getTasksForReview: async (): Promise<Task[]> => {
    const response = await api.get('/tasks/for-review');
    return response.data;
  },
  approveTask: async (taskId: number): Promise<void> => {
    await api.post(`/tasks/${taskId}/approve`);
  },
  rejectTask: async (taskId: number, comment: string): Promise<void> => {
    await api.post(`/tasks/${taskId}/reject`, { comment });
  },
  getTaskCounts: async (): Promise<{ myTasks: number; forReview: number }> => {
    const response = await api.get('/tasks/counts');
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },
};

export const adminAPI = {
  getAllUsers: async (): Promise<User[]> => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  getPendingUsers: async (): Promise<User[]> => {
    const response = await api.get('/admin/users/pending');
    return response.data;
  },
  createUser: async (username: string, email: string, password: string, positionId?: number, isManager?: boolean, role?: string): Promise<User> => {
    const response = await api.post('/admin/users/create', {
      username,
      email,
      password,
      position_id: positionId,
      is_manager: isManager,
      role: role || 'user'
    });
    return response.data.user;
  },
  updateUser: async (userId: number, username?: string, email?: string, password?: string, positionId?: number, isManager?: boolean, role?: string): Promise<void> => {
    await api.put(`/admin/users/${userId}`, {
      username,
      email,
      password,
      position_id: positionId,
      is_manager: isManager,
      role
    });
  },
  approveUser: async (userId: number): Promise<void> => {
    await api.post(`/admin/users/${userId}/approve`);
  },
  deleteUser: async (userId: number): Promise<void> => {
    await api.delete(`/admin/users/${userId}`);
  },
  changeUserRole: async (userId: number, role: string): Promise<void> => {
    await api.put(`/admin/users/${userId}/role`, { role });
  },
  getStats: async (): Promise<any> => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
  setManager: async (userId: number, isManager: boolean): Promise<void> => {
    await api.put(`/admin/users/${userId}/manager`, { is_manager: isManager });
  },
  setUserPosition: async (userId: number, position: string | null): Promise<void> => {
    await api.put(`/admin/users/${userId}/position`, { position });
  },
  assignUserPosition: async (userId: number, positionId: number): Promise<void> => {
    await api.put(`/admin/users/${userId}/assign-position`, { position_id: positionId });
  },
  getUserTasks: async (userId: number): Promise<Task[]> => {
    const response = await api.get(`/admin/users/${userId}/tasks`);
    return response.data;
  },
  getPositions: async (): Promise<Position[]> => {
    const response = await api.get('/admin/positions');
    return response.data;
  },
  createPosition: async (name: string, isManager: boolean): Promise<Position> => {
    const response = await api.post('/admin/positions', { name, is_manager: isManager });
    return response.data;
  },
  updatePosition: async (id: number, name: string, isManager: boolean): Promise<void> => {
    await api.put(`/admin/positions/${id}`, { name, is_manager: isManager });
  },
  deletePosition: async (id: number): Promise<void> => {
    await api.delete(`/admin/positions/${id}`);
  },
  impersonateUser: async (userId: number): Promise<{ token: string; user: User; originalAdmin: User }> => {
    const response = await api.post(`/admin/users/${userId}/impersonate`);
    return response.data;
  },
  returnFromImpersonation: async (): Promise<{ token: string; user: User }> => {
    const response = await api.post('/admin/return-from-impersonation');
    return response.data;
  },
};

export interface Board {
  id: number;
  name: string;
  description?: string;
  created_by: number;
  created_by_username?: string;
  task_count?: number;
  visibility?: 'public' | 'private';
  members?: Array<{
    id: number;
    username: string;
    email: string;
  }>;
  created_at: string;
  updated_at: string;
  columns?: Column[];
}

export interface Column {
  id: number;
  board_id: number;
  name: string;
  position: number;
  tasks?: Task[];
}

export const boardsAPI = {
  getAll: async (): Promise<Board[]> => {
    const response = await api.get('/boards');
    return response.data;
  },
  getById: async (id: number): Promise<Board> => {
    const response = await api.get(`/boards/${id}`);
    return response.data;
  },
  create: async (name: string, description?: string, visibility?: 'public' | 'private', memberIds?: number[]): Promise<Board> => {
    const response = await api.post('/boards', { name, description, visibility, member_ids: memberIds });
    return response.data;
  },
  update: async (id: number, updates: Partial<Board & { member_ids?: number[] }>): Promise<void> => {
    await api.put(`/boards/${id}`, updates);
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/boards/${id}`);
  },
  createColumn: async (boardId: number, name: string, position?: number): Promise<Column> => {
    const response = await api.post(`/boards/${boardId}/columns`, { name, position });
    return response.data;
  },
  updateColumn: async (boardId: number, columnId: number, updates: Partial<Column>): Promise<void> => {
    await api.put(`/boards/${boardId}/columns/${columnId}`, updates);
  },
  deleteColumn: async (boardId: number, columnId: number): Promise<void> => {
    await api.delete(`/boards/${boardId}/columns/${columnId}`);
  },
  addMember: async (boardId: number, userId: number): Promise<User> => {
    const response = await api.post(`/boards/${boardId}/members`, { user_id: userId });
    return response.data;
  },
  removeMember: async (boardId: number, userId: number): Promise<void> => {
    await api.delete(`/boards/${boardId}/members/${userId}`);
  },
};

export interface Notification {
  id: number;
  user_id: number;
  task_id: number | null;
  type: string;
  title: string;
  message: string;
  is_read: number;
  created_at: string;
  task_title?: string;
}

export const notificationsAPI = {
  getAll: async (): Promise<Notification[]> => {
    const response = await api.get('/notifications');
    return response.data;
  },
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/notifications/unread-count');
    return response.data.count;
  },
  markAsRead: async (id: number): Promise<void> => {
    await api.put(`/notifications/${id}/read`);
  },
  markAllAsRead: async (): Promise<void> => {
    await api.put('/notifications/read-all');
  },
  getVapidPublicKey: async (): Promise<{ publicKey: string }> => {
    const response = await api.get('/notifications/vapid-public-key');
    return response.data;
  },
  subscribe: async (subscription: any): Promise<void> => {
    await api.post('/notifications/subscribe', { subscription });
  },
  // unsubscribe отключен - уведомления обязательны
  unsubscribe: async (endpoint: string): Promise<void> => {
    throw new Error('Отписка от уведомлений недоступна. Уведомления обязательны для работы таск-менеджера.');
  },
};

export default api;

